export function parseFootLength(value) {
  const clean = String(value).trim().replace(',', '.');
  if (!/^\d{1,2}(?:\.\d{1,2})?$/.test(clean)) return null;
  const length = Number(clean);
  return length > 0 ? length : null;
}

export function chartMatch(value, rows) {
  const length = parseFootLength(value);
  if (length === null) return { type: 'invalid', indexes: [] };
  const parsed = rows.map((row, index) => {
    if (row == null || String(row).trim() === '') return { missing: true, index };
    const parts = String(row).trim().replace(/,/g, '.').split(/\s*[–—-]\s*/);
    const numbers = parts.map(parseFootLength);
    if (parts.length > 2 || numbers.some(n => n === null)) return null;
    const [min, max = min] = numbers;
    return min <= max ? { min, max, index } : null;
  });
  if (!parsed.length || parsed.some(row => !row)) return { type: 'unavailable', indexes: [] };
  const sorted = parsed.filter(row => !row.missing).sort((a, b) => a.min - b.min);
  if (!sorted.length) return { type: 'unavailable', indexes: [] };
  const matches = sorted.filter(row => length >= row.min && length <= row.max);
  if (matches.length === 1) return { type: 'exact', indexes: [matches[0].index] };
  if (matches.length > 1) return { type: 'unavailable', indexes: [] };
  if (length < sorted[0].min || length > Math.max(...sorted.map(row => row.max))) return { type: 'outside', indexes: [] };
  const lower = sorted.filter(row => row.max < length).sort((a, b) => b.max - a.max)[0];
  const upper = sorted.find(row => row.min > length);
  if (lower && upper && parsed.some(row => row.missing && row.index > Math.min(lower.index, upper.index) && row.index < Math.max(lower.index, upper.index))) {
    return { type: 'unavailable', indexes: [] };
  }
  return lower && upper ? { type: 'between', indexes: [lower.index, upper.index] } : { type: 'unavailable', indexes: [] };
}

if (typeof customElements !== 'undefined' && !customElements.get('leavys-fit-guide')) {
  let fitInstance = 0;
  class LeavysFitGuide extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      const details = this.querySelector('.lf-details');
      const trigger = details?.querySelector('summary');
      const content = this.querySelector('.lf-content');
      if (!details || !trigger || !content || typeof HTMLDialogElement === 'undefined') return;
      const id = `leavys-fit-${++fitInstance}`;
      const dialog = document.createElement('dialog');
      dialog.className = 'lf-dialog lf-enhanced';
      dialog.id = id;
      const title = content.querySelector('h2');
      title.id = `${id}-title`;
      dialog.setAttribute('aria-labelledby', title.id);
      dialog.append(content);
      this.append(dialog);
      this.dialog = dialog;
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-controls', id);
      trigger.setAttribute('aria-expanded', 'false');
      const close = content.querySelector('[data-fit-close]');
      close.hidden = false;
      close.addEventListener('click', () => dialog.close());
      trigger.addEventListener('click', event => {
        event.preventDefault();
        dialog.showModal();
        trigger.setAttribute('aria-expanded', 'true');
        close.focus();
      });
      dialog.addEventListener('close', () => { trigger.setAttribute('aria-expanded', 'false'); trigger.focus(); });
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const bounds = dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
      });
      const tabs = [...content.querySelectorAll('[data-fit-tab]')];
      const panels = [...content.querySelectorAll('[data-fit-panel]')];
      const activate = tab => {
        tabs.forEach(t => { const on = t === tab; t.setAttribute('aria-selected', String(on)); t.tabIndex = on ? 0 : -1; });
        panels.forEach(panel => { panel.hidden = panel.dataset.fitPanel !== tab.dataset.fitTab; });
        content.querySelector('.lf-scroll').scrollTop = 0;
      };
      tabs.forEach((tab, index) => {
        const panel = panels.find(p => p.dataset.fitPanel === tab.dataset.fitTab);
        tab.id = `${id}-tab-${index}`;
        panel.id = `${id}-panel-${index}`;
        tab.setAttribute('aria-controls', panel.id);
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tab.id);
        panel.tabIndex = 0;
        tab.addEventListener('click', () => activate(tab));
        tab.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft') next = (index + tabs.length - 1) % tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          if (next !== undefined) { event.preventDefault(); activate(tabs[next]); tabs[next].focus(); }
        });
      });
      content.querySelector('.lf-nav').hidden = false;
      activate(tabs[0]);
      const concern = content.querySelector('[data-fit-concern]');
      const answers = [...content.querySelectorAll('[data-fit-answer]')];
      const selectAnswer = () => answers.forEach(answer => { answer.hidden = answer.dataset.fitAnswer !== concern.value; });
      const label = content.querySelector(`label[for="${concern.id}"]`);
      concern.id = `${id}-concern`;
      label.htmlFor = concern.id;
      concern.hidden = false;
      concern.addEventListener('change', selectAnswer);
      selectAnswer();
      const helper = content.querySelector('[data-fit-cm-helper]');
      if (helper) {
        const input = helper.querySelector('[data-fit-cm]');
        const result = helper.querySelector('[data-fit-cm-result]');
        const rows = [...content.querySelectorAll('[data-fit-row]')];
        const lengths = rows.map(row => row.dataset.footCm);
        const clear = () => {
          rows.forEach(row => { row.classList.remove('lf-row-match'); row.removeAttribute('aria-current'); });
          result.textContent = '';
          input.removeAttribute('aria-invalid');
        };
        const check = () => {
          clear();
          const match = chartMatch(input.value, lengths);
          if (match.type === 'invalid') input.setAttribute('aria-invalid', 'true');
          match.indexes.forEach(index => { rows[index].classList.add('lf-row-match'); rows[index].setAttribute('aria-current', 'true'); });
          result.textContent = helper.dataset[match.type]
            .replace('[size]', rows[match.indexes[0]]?.dataset.eu || '')
            .replace('[lower]', rows[match.indexes[0]]?.dataset.eu || '')
            .replace('[upper]', rows[match.indexes[1]]?.dataset.eu || '');
        };
        input.id = `${id}-cm`;
        helper.querySelector('label').htmlFor = input.id;
        helper.querySelector('.lf-small').id = `${id}-cm-hint`;
        result.id = `${id}-cm-result`;
        input.setAttribute('aria-describedby', `${id}-cm-hint ${id}-cm-result`);
        input.addEventListener('input', clear);
        input.addEventListener('keydown', event => { if (event.key === 'Enter') { event.preventDefault(); check(); } });
        helper.querySelector('[data-fit-cm-check]').addEventListener('click', check);
        helper.querySelector('[data-fit-measure]').addEventListener('click', () => {
          const measureTab = tabs.find(tab => tab.dataset.fitTab === 'measure');
          activate(measureTab);
          measureTab.focus();
        });
        if (chartMatch('1', lengths).type !== 'unavailable') helper.hidden = false;
      }
    }
    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
    }
  }
  customElements.define('leavys-fit-guide', LeavysFitGuide);
}
